"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MagneticButton } from "@/components/ui/magnetic-button";
import { SceneAtmosphere } from "@/components/scroll/scene-atmosphere";
import { useScrollTriggerRefresh } from "@/lib/use-scroll-trigger-refresh";

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

export function JourneyCta({
  heading,
  body,
  primaryLabel = "Start a conversation",
  secondaryHref,
  secondaryLabel,
}: {
  heading: string;
  body: string;
  primaryLabel?: string;
  secondaryHref?: string;
  secondaryLabel?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-meta]",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.08,
          ease: "power3.out",
          duration: 0.8,
          scrollTrigger: { trigger: "[data-cta-content]", start: "top 85%", toggleActions: "play none none reverse" },
        }
      );
    }, root);

    return () => ctx.revert();
  }, []);

  useScrollTriggerRefresh([]);

  return (
    <section ref={rootRef} className="relative flex items-center overflow-hidden px-6 py-12 sm:min-h-[62vh] sm:py-16 lg:px-10">
      <SceneAtmosphere x="50%" y="50%" intensity={0.2} size={70} />
      <div data-cta-content className="relative z-10 mx-auto max-w-3xl text-center">
        <h2 data-meta className="font-display text-[clamp(2rem,5vw,4rem)] font-bold leading-[1.0] tracking-[-0.02em] text-[var(--color-text-primary)] text-balance">
          {heading}
        </h2>
        <p data-meta className="mx-auto mt-6 max-w-lg text-lg leading-relaxed text-[var(--color-text-secondary)] text-pretty">
          {body}
        </p>
        <div data-meta className="mt-9 flex flex-wrap items-center justify-center gap-4">
          <MagneticButton
            href="/contact"
            className="inline-flex items-center rounded-full bg-[var(--color-primary)] px-8 py-4 text-base font-medium text-white transition-colors hover:bg-[var(--color-primary-dark)]"
          >
            {primaryLabel}
          </MagneticButton>
          {secondaryHref && secondaryLabel && (
            <Link
              href={secondaryHref}
              className="inline-flex items-center rounded-full border border-[var(--color-border)] px-7 py-4 text-base font-medium text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-primary)]"
            >
              {secondaryLabel}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
